"use strict";

class CCartRecommendation extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;

    this.wrapper = null;
    this.swiper = null;
    this.btnPrev = null;
    this.btnNext = null;
    this.forms = [];

    this.prevSlide = this.prevSlide.bind(this);
    this.nextSlide = this.nextSlide.bind(this);

    this.initialize();
  }

  disconnectedCallback() {
    this.btnPrev?.removeEventListener("click", this.prevSlide);
    this.btnNext?.removeEventListener("click", this.nextSlide);

    this.forms?.forEach((form) => {
      if (form.__cartRecommendationSubmit) {
        form.removeEventListener("submit", form.__cartRecommendationSubmit);
      }
    });

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  initialize() {
    const slides = this.querySelectorAll(".swiper-slide");

    if (slides.length > 0) {
      this.initializeSlider();
      return;
    }

    this.renderFallbackProducts();
  }

  renderFallbackProducts() {
    if (!this.sectionId || !this.productId) {
      this.remove();
      return;
    }

    fetch(
      `${window.Shopify.routes.root}recommendations/products?product_id=${this.productId}&limit=20&section_id=${this.sectionId}&intent=related`
    )
      .then((response) => response.text())
      .then((html) => {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;

        const newRecommendation = wrapper.querySelector("c-cart-recommendation");

        if (!newRecommendation) {
          this.remove();
          return;
        }

        this.innerHTML = newRecommendation.innerHTML;

        const slides = this.querySelectorAll(".swiper-slide");

        if (!slides.length) {
          this.remove();
          return;
        }

        this.initializeSlider();
      })
      .catch((error) => {
        console.warn("[CCartRecommendation] fallback failed:", error);
        this.remove();
      });
  }

  initializeSlider() {
    this.wrapper = this.querySelector(".js-cart-recommendation__list-wrapper");

    if (!this.wrapper || typeof Swiper === "undefined") return;

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.forms = Array.from(this.querySelectorAll(".js-cart-recommendation__form"));

    this.swiper = new Swiper(this.wrapper, {
      slidesPerView: "auto",
      spaceBetween: 12,
      speed: 400,
      slidesOffsetAfter: 40,
      autoplay: {
        delay: 10000,
        disableOnInteraction: false
      }
    });

    this.btnPrev = this.querySelector(".js-cart-recommendation__button--prev");
    this.btnNext = this.querySelector(".js-cart-recommendation__button--next");

    this.btnPrev?.removeEventListener("click", this.prevSlide);
    this.btnNext?.removeEventListener("click", this.nextSlide);

    this.btnPrev?.addEventListener("click", this.prevSlide);
    this.btnNext?.addEventListener("click", this.nextSlide);

    this.forms.forEach((form) => {
      const handler = (event) => this.onSubmitHandler(form, event);

      form.__cartRecommendationSubmit = handler;
      form.addEventListener("submit", handler);
    });

    this.slideChange();

    this.swiper.on("slideChange", () => {
      this.slideChange();
    });

    setTimeout(() => {
      this.swiper?.update();
      this.slideChange();
    }, 100);
  }

  onSubmitHandler(form, event) {
    event.preventDefault();

    const onlyDefaultVariant = form.dataset.onlyDefaultVariant === "true";
    const variantId = form.dataset.variantId;
    const variantUrl = form.dataset.variantUrl;
    const button = form.querySelector(".js-cart-recommendation__submit-btn");

    if (!onlyDefaultVariant) {
      window.PubSub?.publish("variantModal:open", variantUrl);
      return;
    }

    if (!variantId) return;

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");

    button?.setAttribute("disabled", "");

    // JS-03 (#126): Shopify answers a refusal with HTTP 422 and a JSON body, which response.json()
    // parses happily, so the success arm used to run and the drawer slid open over an unchanged
    // cart. The route was hard-coded too, which dropped the locale prefix on /de, /es, /fr, /ja
    // and /zh.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

    fetch(`${root}cart/add.js`, {
      method: "POST",
      body: formData,
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then((response) =>
        response.json().then(
          (data) => ({ ok: response.ok, data }),
          () => ({ ok: response.ok, data: null })
        )
      )
      .then(({ ok, data }) => {
        button?.removeAttribute("disabled");

        const error = form.querySelector(".js-cart-recommendation__error");

        if (!ok) {
          if (error) {
            if (data?.description) error.textContent = data.description;
            error.hidden = false;
          }
          return;
        }

        if (error) error.hidden = true;
        /* A11Y-02 (#109): the added line travels with the event so the drawer can announce it */
        window.PubSub?.publish("cart-modal:open", data);
      })
      .catch((error) => {
        console.error("[CCartRecommendation] add to cart failed:", error);
        button?.removeAttribute("disabled");
      });
  }

  slideChange() {
    if (!this.swiper) return;

    if (this.swiper.isBeginning) {
      this.btnPrev?.setAttribute("disabled", "");
    } else {
      this.btnPrev?.removeAttribute("disabled");
    }

    if (this.swiper.isEnd) {
      this.btnNext?.setAttribute("disabled", "");
    } else {
      this.btnNext?.removeAttribute("disabled");
    }

    if (this.swiper.slides.length === this.swiper.activeIndex + 1) {
      this.wrapper?.classList.remove("is-scroll");
      this.btnNext?.setAttribute("disabled", "");
    } else {
      this.wrapper?.classList.add("is-scroll");
    }
  }

  prevSlide() {
    this.swiper?.slidePrev();
  }

  nextSlide() {
    this.swiper?.slideNext();
  }
}

if (!customElements.get("c-cart-recommendation")) {
  customElements.define("c-cart-recommendation", CCartRecommendation);
}
